import React, { useState, useEffect } from 'react';
import useCacheManager from '../hooks/useCacheManager';

const CacheManager = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [newVersion, setNewVersion] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    isClearing,
    lastCleared,
    currentVersion,
    clearAllCache,
    clearCacheManually,
    getCacheInfo,
    forceVersionUpdate,
    APP_VERSION,
    STORAGE_KEYS_TO_CLEAR
  } = useCacheManager();

  useEffect(() => {
    if (isVisible) {
      refreshCacheInfo();
    }
  }, [isVisible]);

  const refreshCacheInfo = async () => {
    setIsRefreshing(true);
    try {
      const info = await getCacheInfo();
      setCacheInfo(info);
    } catch (error) {
      console.error('Error refreshing cache info:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearCache = async (reason = 'manual') => {
    const success = await clearCacheManually(reason);
    if (success) {
      await refreshCacheInfo();
    }
  };

  const handleForceVersion = async () => {
    if (newVersion.trim()) {
      const success = await forceVersionUpdate(newVersion.trim());
      if (success) {
        setNewVersion('');
        await refreshCacheInfo();
      }
    }
  };

  // Solo mostrar en desarrollo
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className={`fixed bottom-4 left-4 z-50 rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-colors ${
          isClearing ? 'bg-orange-500 hover:bg-orange-600' :
          lastCleared ? 'bg-green-500 hover:bg-green-600' :
          'bg-blue-500 hover:bg-blue-600'
        } text-white`}
        title="Cache Manager"
        disabled={isClearing}
      >
        {isClearing ? '🔄' : lastCleared ? '✅' : '🗄️'}
      </button>

      {/* Panel del cache manager */}
      {isVisible && (
        <div className="fixed bottom-20 left-4 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-96 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Cache Manager</h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {/* Información de versión */}
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="text-sm">
                <div><strong>Versión Actual:</strong> {currentVersion}</div>
                <div><strong>APP_VERSION:</strong> {APP_VERSION}</div>
                {lastCleared && (
                  <div><strong>Última Limpieza:</strong> {new Date(lastCleared).toLocaleString()}</div>
                )}
              </div>
            </div>

            {/* Estado del cache */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Estado del Cache</h4>
                <button
                  onClick={refreshCacheInfo}
                  disabled={isRefreshing}
                  className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                >
                  {isRefreshing ? '🔄' : '🔄'}
                </button>
              </div>
              
              {cacheInfo ? (
                <div className="text-sm">
                  <div><strong>Total de claves:</strong> {cacheInfo.totalKeys}</div>
                  <div><strong>Claves principales:</strong></div>
                  <div className="max-h-20 overflow-y-auto text-xs">
                    {Object.keys(cacheInfo.keys).slice(0, 10).map(key => (
                      <div key={key} className="flex justify-between">
                        <span className="truncate">{key}</span>
                        <span className="ml-2">
                          {cacheInfo.keys[key].hasValue ? 
                            `${cacheInfo.keys[key].size} chars` : 
                            'empty'
                          }
                        </span>
                      </div>
                    ))}
                    {Object.keys(cacheInfo.keys).length > 10 && (
                      <div className="text-gray-500">... y {Object.keys(cacheInfo.keys).length - 10} más</div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  {isRefreshing ? 'Cargando...' : 'No hay información disponible'}
                </div>
              )}
            </div>

            {/* Acciones de limpieza */}
            <div className="space-y-2">
              <button
                onClick={() => handleClearCache('development')}
                disabled={isClearing}
                className="w-full bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition-colors text-sm disabled:opacity-50"
              >
                {isClearing ? 'Limpiando...' : 'Limpiar Cache (Dev)'}
              </button>
              
              <button
                onClick={() => handleClearCache('testing')}
                disabled={isClearing}
                className="w-full bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600 transition-colors text-sm disabled:opacity-50"
              >
                {isClearing ? 'Limpiando...' : 'Limpiar Cache (Testing)'}
              </button>
            </div>

            {/* Forzar cambio de versión */}
            <div className="bg-yellow-50 p-3 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Forzar Cambio de Versión</h4>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newVersion}
                  onChange={(e) => setNewVersion(e.target.value)}
                  placeholder="Nueva versión (ej: 1.0.1)"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <button
                  onClick={handleForceVersion}
                  disabled={!newVersion.trim() || isClearing}
                  className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600 disabled:opacity-50 text-sm"
                >
                  Cambiar
                </button>
              </div>
            </div>

            {/* Claves a limpiar */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Claves Configuradas</h4>
              <div className="text-xs text-gray-600">
                {STORAGE_KEYS_TO_CLEAR.map(key => (
                  <div key={key} className="mb-1">• {key}</div>
                ))}
              </div>
            </div>

            {/* Información */}
            <div className="text-xs text-gray-500 text-center">
              Solo visible en desarrollo • Cache Manager
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CacheManager;
